<template>
<div class="voucher-v2-wrapper">
  <div class="voucher-v2-item">
    <div class="unavailable-mask" v-if="!voucherBg.isAvailableBg && !isShowUseNow"></div>
    <div class="voucher-info-box">
      <img :src="$cvWebpFn(voucherBg.bgUrl, { w: 400 })" alt="" class="voucher-info-box-bg">
      <div class="info-inner">
        <p class="voucher-price" :color-attr="voucherBg.colorAttr">
          {{ $currencySymbol() }} {{ $toThousandsFn(item.amount) }}
        </p>
        
        <!-- v-if="item.voucherType === 1"  -->
        <p 
          class="voucher-type"
          :color-attr="voucherBg.colorAttr"
        >
          {{ voucherBg.label }}
        </p>
        
        <p class="voucher-tips">{{ voucherTips }}</p>
      </div>
    </div>
    <div class="voucher-opt-box" v-if="!hiddenOpt">
      <div class="check-box" v-if="isCheck">
        <van-checkbox :name="item.voucherId" @click="handleVoucherCheck(item)" />
      </div>
      <template v-else>
        <slot name="operation-slot" :data="voucherBg">
          <div 
            v-if="(isShowUseNow || item.status === 1) && new Date(item.startDate) <= new Date()"
            :class="['opt-btn', voucherBg.btnClassName]"
            @click="toUseFn"
          >
            Use Now
          </div>
          <div 
            v-if="item.status === 1 && new Date(item.startDate) > new Date()"
            :class="['opt-btn soon-btn']"
          >
            Starts Soon
          </div>
          <div
            v-if="isGetNow"
            :class="['opt-btn', voucherBg.btnClassName]"
            @click="postVoucherFn"
          >
            Claim Now
          </div>
        </slot>
        <div class="icon">
          <!-- 已领取 -->
          <img v-if="item.isEarned && !item.status" v-webp src="https://image.kilimall.com/lite/new_wap/voucher/claimed.png" />
          <!-- 券已抢完， 无库存 -->
          <img v-else-if="item.remainNumber <= 0"  v-webp src="https://image.kilimall.com/lite/new_wap/voucher/grabbed.png" />
          <!-- 券过期 -->
          <img v-if="item.status === 3 || item.status === 4"  v-webp src="https://image.kilimall.com/lite/new_wap/voucher/expired.png" />
          <!-- 券即将过期 -->
          <!-- <img v-else-if="item.status === 1 && isSoonFn()"  v-webp src="https://image.kilimall.com/lite/new_wap/voucher/expire-soon.png" /> -->
          <!-- 券已使用 -->
          <img v-else-if="item.status === 2"  v-webp src="https://image.kilimall.com/lite/new_wap/voucher/used.png" />
        </div>
      </template>
      <p class="valid-time" v-if="item.isEarned !== false && effectiveEndDate && effectiveStartDate && !isEndsToday">
        Valid:  {{ $tz.dateFormatEnFn(effectiveStartDate, 2)}}-{{ $tz.dateFormatEnFn(effectiveEndDate, 2) }}
      </p>
      <p class="valid-time" v-if="item.isEarned !== false && effectiveEndDate && effectiveStartDate && isEndsToday">
        Expires soon, <br>Valid: <span class="time-span">{{ countdownStr }}</span>
      </p>
    </div>
  </div>

  <div class="reasons" v-if="showReasons && (item as IInvalidVoucher)?.reasons?.length">
    <p v-for="reason in (item as IInvalidVoucher)?.reasons" :key="reason">
      <span class="dot">·</span>
      <span>{{ reason }}</span>
    </p>
  </div>
</div>
</template>

<script setup lang="ts">
import { type IInvalidVoucher, type Voucher, type ReceiveScene, postVoucher } from '@/api/models/userInfo'
import { showToast } from 'vant';
import {
  buildVoucherListingSource,
  buildVoucherSourceValue,
  type VoucherLatestPage,
  type VoucherSourceScene
} from '@/plugins/utils'

const props = withDefaults(
  defineProps<{
    item: Voucher | IInvalidVoucher,
    showType?: 'list' | 'store',
    isCheck?: boolean,
    showReasons?: boolean
    hiddenOpt?: boolean
    /** 优惠券页场景：领券中心 / 我的优惠券 */
    sourceScene?: VoucherSourceScene
    receiveScene?: ReceiveScene
  }>(),
  {
    showType: 'list'
  }
)
const startDate = ref('')
const endDate = ref('')
const isShowUseNow = ref(false)
const emits = defineEmits<{
  (event: 'callback'): void
  (event: 'voucher:check', item: Voucher): void
}>()

const { $currencySymbol, $tz, $toListingDetail, $toThousandsFn } = useNuxtApp()
// 这里上传一下 AWS
const voucherBgMap: IAnyObject = {
  '1-1': { bgUrl: 'https://image.kilimall.com/kenya/new_wap/voucher/bg_all.png', colorAttr: 'platform', btnClassName: 'opt-btn__1-1', label: 'Platform voucher' },
  '1-2': { bgUrl: 'https://image.kilimall.com/kenya/new_wap/voucher/bg_allstore.png', colorAttr: 'compensate', btnClassName: 'opt-btn__1-2', label: 'Gift voucher' },
  '1-3': { bgUrl: 'https://image.kilimall.com/kenya/new_wap/voucher/bg_logistics.png', colorAttr: 'shipping', btnClassName: 'opt-btn__1-3', label: 'Shipping voucher' },
  '2-1': { bgUrl: 'https://image.kilimall.com/kenya/new_wap/voucher/bg_store.png', colorAttr: 'store', btnClassName: 'opt-btn__2-1', label: 'Store voucher' }
  // 匹配不到就用这个
  // default: { bgUrl: 'https://image.kilimall.com/kenya/new_wap/voucher/bg_store.png', btnClassName: '' }
}

const voucherTips = computed<string>(() => {
  const { voucherType, usageScope, notice, limitType, isLimit, limitBillAmount, ownerType, storeName } = props.item

  // 店铺页显示优惠券
  // if(props.showType === 'store') {
  //   return `Available above KSh ${limitBillAmount}`
  // }

  if(voucherType === 3) {
    return notice
  }

  let _tips = ['Available']

  let limitText = ''
  
  if(!usageScope) {
    if(limitType === 2) {
      limitText = isLimit ? 'for specific products' : 'width specific products'
    } else {
      limitText = isLimit ? 'for all products' : 'with all products'
    }
  } else {
    limitText = isLimit ? `for ${usageScope}` : `with ${usageScope}`
  }

  if(isLimit) {
    _tips.push(`above ${$currencySymbol()} ${$toThousandsFn(limitBillAmount)}`, limitText)
  } else {
    _tips.push(limitText)
  }

  if (ownerType === 2 ) {
    // 指定店铺券
    _tips.push(`in ${storeName} store`)
  } else {
    if(ownerType === 1 && limitType === 1 && !isLimit) {
      // 平台券、全品类、无门槛
      _tips.push('in all stores')
    }
  }

  return _tips.join(' ')
})

const isGetNow = computed<boolean>(() => {
  const { status, isEarned, remainNumber } = props.item

  /** 
   * status： status: 1 未使用；2 已使用；3 已过期；4  已失效
   * 可领取数量 > 0, 并且为领取过 
   * */
  return (!status || status <= 0) && remainNumber > 0 && !isEarned
})

const voucherBg = computed<{
  url: string,
  isAvailableBg: boolean,
  btnClassName: string,
  bgUrl: string,
  colorAttr: string,
  label: string
}>(() => {
  const { voucherType, ownerType, status, genId } = props.item

  const isAvailableBg = status === 1 || isGetNow.value
  const key = `${ownerType}-${voucherType}`

  return {
    ...voucherBgMap[key],
    isAvailableBg
  }
})

/** 领取优惠券 */
const postVoucherFn = async () => { 
  const params = {
    genId: props.item.genId,
    receiveScene: props.receiveScene
  }
  
  const res = await postVoucher(params, false)
  if(res && res.code === 0) {
    startDate.value = res.data?.startDate || ''
    endDate.value = res.data?.endDate || ''
    isShowUseNow.value = true
    // 通知父组件刷新数据
    emits('callback')
    showToast('Successfully claimed!  Use it now!')
  }
}

const effectiveEndDate = computed(() => endDate.value || props.item.endDate)
const effectiveStartDate = computed(() => startDate.value || props.item.startDate)

const isEndsToday = computed(() => {
  if (!effectiveEndDate.value) return false
  // 使用当地时区格式化日期进行比较
  const endLocal = $tz.zeroToLocalZonetime(effectiveEndDate.value, { format: 'yyyy-MM-dd' })
  const nowLocal = $tz.zeroToLocalZonetime(new Date(), { format: 'yyyy-MM-dd' })
  return endLocal === nowLocal
})

const countdownStr = ref('')
let timer: any = null

const updateCountdown = () => {
  if (!effectiveEndDate.value) return
  // 直接用 UTC 时间戳计算差值，时间差与时区无关
  const endUtc = new Date(effectiveEndDate.value).getTime()
  const nowUtc = Date.now()
  const diff = endUtc - nowUtc
  
  if (diff <= 0) {
    countdownStr.value = '00:00:00'
    return
  }
  
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  
  const pad = (n: number) => n.toString().padStart(2, '0')
  countdownStr.value = `${pad(h)}:${pad(m)}:${pad(s)}`
}

onMounted(() => {
  updateCountdown()
  timer = setInterval(updateCountdown, 1000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

const handleViewNowClick = () => {
  useRouter().push('/voucher')
}

/** 点击用券 */
const toUseFn = () => {
  const item = props.item
  const sourceValue = buildVoucherSourceValue(item)

  if (props.sourceScene) {
    const navigateWithSource = (latestPage: VoucherLatestPage, path: string) => {
      const source = buildVoucherListingSource(props.sourceScene!, sourceValue, latestPage)
      useRouter().push(`${path}${path.includes('?') ? '&' : '?'}source=${encodeURIComponent(source)}`)
    }

    if (item.limitType === 3 && item.limiter?.listingIds?.length) {
      const listingId = Number(item.limiter.listingIds[0])
      if (listingId) {
        $toListingDetail(listingId, {
          source: buildVoucherListingSource(props.sourceScene, sourceValue, 'voucherList')
        })
        return
      }
    }

    if (item.ownerType === 1) {
      if (item.limiter?.categoryIds?.length > 0) {
        const ctgs = item.limiter.categoryIds.slice(0, 200).join('_')
        navigateWithSource('searchProductResults', `/search?backCid=${ctgs}`)
        return
      }

      useRouter().push('/')
      return
    }

    navigateWithSource('store', `/store/${item.storeId}`)
    return
  }

  // 非优惠券页场景，保持原逻辑
  if (item.ownerType === 1) {
    if (item.limiter?.categoryIds.length > 0) {
      const ctgs = item.limiter.categoryIds.slice(0, 200).join('_')
      useRouter().push(`/search?backCid=${ctgs}&source=accountCenter|voucherCategory|${item.name}`)
    } else {
      useRouter().push('/')
    }
  } else {
    useRouter().push(`/store/${item.storeId}?source=accountCenter|voucherStore|${item.name}`)
  }
}

const handleVoucherCheck = (item: Voucher) => {
  emits('voucher:check', item)
}
</script>

<style lang="scss" scoped>
$voucherHeight: 232px;
.reasons{
  margin-top: 15px;
  p{
    font-size: 24px;
    color: #E14646;
    padding: 0 10px;
    display: flex;
    .dot{
      margin-right: 8px;
    }
  }
}
.voucher-v2-wrapper{
  background-color: #fff;
  margin-bottom: 20px;
  padding: 16px;
}
.voucher-v2-item{
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  cursor: pointer;
  .unavailable-mask{
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba($color: #999, $alpha: 0.4);
    z-index: 2;
  }
  .voucher-info-box{
    padding: 12px;
    flex: 1;
    height: $voucherHeight;
    margin-right: 16px;
    position: relative;
    .voucher-info-box-bg{
      position: absolute;
      top: 0;
      left: 0;
      max-width: 100%;
      height: 100%;
      object-fit: contain;
      z-index: 0;
    }
    .info-inner{
      position: relative;
      height: $voucherHeight;
    }
    .voucher-price{
      font-size: 48px;
      font-weight: 900;
    }
    .voucher-type{
      font-size: 24px;
      font-weight: 400;
      background-color: #fff;
      display: inline-block;
      padding: 8px 20px;
      border-radius: 40px;
    }
    .voucher-price[color-attr=platform]{ color: #F76900; }
    .voucher-type[color-attr=platform]{ color: #F87B2B; }

    .voucher-price[color-attr=compensate]{ color: #FFB615; }
    .voucher-type[color-attr=compensate]{ color: #FFB716; }

    .voucher-price[color-attr=shipping]{ color: #00CC66; }
    .voucher-type[color-attr=shipping]{ color: #00CC66; }

    .voucher-price[color-attr=store]{ color: #F76900; }
    .voucher-type[color-attr=store]{ color: #F87B2B; }

    .voucher-tips{
      font-size: 24px;
      color: #333333;
      // line-height: px;
      position: absolute;
      bottom: 32px;
      @include ellipsisMultiline(2);
    }
  }
  .voucher-opt-box{
    width: 230px;
    height: $voucherHeight;
    border: 1.65px solid #F1F1F1;
    position: relative;
    text-align: center;
    @include center();
    .icon{
      position: absolute;
      top: 0;
      right: 0;
      img{
        max-width: 100%;
        max-height: 100%;
      }
    }
    .opt-btn{
      font-weight: 700;
      font-size: 20px;
      color: #FFFFFF;
      background-color: var(--default_color);
      width: 140px;
      height: 48px;
      line-height: 48px;
      border-radius: 1.5px;
      margin-bottom: 30px;
      z-index: 9;
    }
    .check-box{
      margin-bottom: 30px;
    }
    .opt-btn__1-1{
      background-image: linear-gradient(90deg, #FF6101 0%, #FF9C61 100%);
    }
    .opt-btn__1-2 {
      background-image: linear-gradient(90deg, #FFB615 0%, #FEDC70 100%);
    }
    .opt-btn__1-3 {
      background-image: linear-gradient(90deg, #30D65E 0%, #50E7A8 100%);
    }
    .opt-btn__2-1 {
      background-image: linear-gradient(90deg, #FF6101 0%, #FF9C61 100%);
    }
    .to-use{
      background-image: linear-gradient(90deg, #FF6101 0%, #FF9C61 100%);
    }
    .soon-btn {
      background-color: #E4E4E4;
      color: #999999;
    }
    .valid-time{
      position: absolute;
      bottom: 28px;
      width: 100%;
      font-size: 20px;
      color: #666666;
      text-align: center;
      line-height: 28px;
      .time-span {
        color: red;
      }
    }
  }
}
</style>
